import { BodyEmailTemplate, CanonicalLoad, UserEmailTemplates, UserNylasEmails } from '@numeo/types-core'
import { AxiosError } from 'axios'
import { useAuth } from 'hooks/auth'
import { useReadScopeGuard } from 'hooks/useReadScopeGuard'
import { useCallback, useEffect, useRef, useState } from 'react'
import { showSnackbar, SnackbarType } from 'utils/snackbar'
import { sendPrivateLoadEmailV4 } from '../api/emailApi'
import { fetchEmailThreadIds } from '../api/loads.api'
import { convertCanonicalLoadToV4Payload } from '../utils/emailPayload'
import { generateEmailContentFromTemplate } from '../utils/emailTemplateUtils'
import { resolveSenderCompanyData } from '../utils/resolveSenderCompanyData'
import { getLoadUid } from '../utils/loadKey'
import { trackEmailAttempted, trackEmailCompleted } from '../utils/sourceAnalytics'
import {
    toServerEmailAnalytics,
    trackEmailRequestFailed,
    trackEmailSendSubmitted,
    trackEmailValidationFailed,
    trackLoadContactClicked,
} from '../utils/loadAttributionAnalytics'

export interface EmailConfig {
    selectedEmail: string
    selectedTemplateId: string
}

// Module-level in-memory config — each browser tab has its own JS context,
// so this is NOT shared across tabs (unlike localStorage).
// On page refresh, this resets to null and re-initializes from isPrimary.
let currentEmailConfig: EmailConfig | null = null

/** Read the current in-memory email config (used by OfferBookEmailCompose) */
export const getCurrentEmailConfig = (): EmailConfig | null => currentEmailConfig

/** Set the in-memory email config outside the hook (e.g. the Box Truck draft-email sender selector,
 *  which reuses the same module singleton so its OfferBookEmailCompose picks up the chosen sender). */
export const setCurrentEmailConfig = (config: EmailConfig): void => {
    currentEmailConfig = config
}

const loadEmailThreads = (): Map<string, string> => {
    try {
        const raw = localStorage.getItem('private-loads-threads')
        if (!raw) return new Map()
        const obj: Record<string, string> = JSON.parse(raw)
        return new Map(Object.entries(obj))
    } catch {
        return new Map()
    }
}

const saveEmailThread = (loadUid: string, threadId: string) => {
    try {
        const raw = localStorage.getItem('private-loads-threads')
        const obj: Record<string, string> = raw ? JSON.parse(raw) : {}
        obj[loadUid] = threadId
        localStorage.setItem('private-loads-threads', JSON.stringify(obj))
    } catch {
        // ignore
    }
}

const loadLoadMongoIds = (): Map<string, string> => {
    try {
        const raw = localStorage.getItem('private-loads-mongo-ids')
        if (!raw) return new Map()
        const obj: Record<string, string> = JSON.parse(raw)
        return new Map(Object.entries(obj))
    } catch {
        return new Map()
    }
}

const saveLoadMongoId = (loadUid: string, mongoId: string) => {
    try {
        const raw = localStorage.getItem('private-loads-mongo-ids')
        const obj: Record<string, string> = raw ? JSON.parse(raw) : {}
        obj[loadUid] = mongoId
        localStorage.setItem('private-loads-mongo-ids', JSON.stringify(obj))
    } catch {
        // ignore
    }
}

const resolveEmailAndTemplate = (
    nylasEmails: UserNylasEmails[],
    config: EmailConfig | null,
    userEmailTemplates?: UserEmailTemplates
): { nylasEmail: UserNylasEmails; template: BodyEmailTemplate | undefined } | null => {
    if (!nylasEmails || nylasEmails.length === 0) return null

    let nylasEmail: UserNylasEmails | undefined

    if (config?.selectedEmail) {
        nylasEmail = nylasEmails.find((e) => e.email === config.selectedEmail)
    }
    if (!nylasEmail) {
        nylasEmail = nylasEmails.find((e) => e.isPrimary) || nylasEmails[0]
    }

    const templates = nylasEmail.emailTemplates?.bodyTemplates || []
    let template: BodyEmailTemplate | undefined

    if (config?.selectedTemplateId) {
        template = templates.find((t) => t._id === config.selectedTemplateId)
    }
    if (!template && templates.length > 0) {
        template = templates.find((t) => t.isPrimary) || templates[0]
    }

    // Fallback to user-level emailTemplates if selected email has no templates
    if (!template && userEmailTemplates?.bodyTemplates?.length) {
        template = userEmailTemplates.bodyTemplates[0]
    }

    return { nylasEmail, template }
}

export const usePrivateLoadEmail = () => {
    const { user, application, organizationData } = useAuth()
    const ensureReadScope = useReadScopeGuard()
    const sendEmailRef = useRef<(load: CanonicalLoad) => void>()
    const [sendingLoads, setSendingLoads] = useState<Set<string>>(new Set())
    const [errorLoads, setErrorLoads] = useState<Set<string>>(new Set())
    const [emailThreads, setEmailThreads] = useState<Map<string, string>>(() => loadEmailThreads())
    const [unreadCounts, setUnreadCounts] = useState<Map<string, number>>(new Map())
    const [loadMongoIds, setLoadMongoIds] = useState<Map<string, string>>(() => loadLoadMongoIds())

    // Initialize in-memory config from isPrimary when user loads (only if not yet set by selector)
    useEffect(() => {
        if (!user?.nylasEmails?.length || currentEmailConfig) return
        const primaryEmail = user.nylasEmails.find((e) => e.isPrimary) || user.nylasEmails[0]
        if (!primaryEmail) return
        const templates = primaryEmail.emailTemplates?.bodyTemplates || user.emailTemplates?.bodyTemplates || []
        const templateId = (templates.find((t) => t.isPrimary) || templates[0])?._id || ''
        currentEmailConfig = { selectedEmail: primaryEmail.email, selectedTemplateId: templateId }
    }, [user?.nylasEmails, user?.emailTemplates?.bodyTemplates])

    const updateEmailConfig = useCallback((config: EmailConfig) => {
        setCurrentEmailConfig(config)
    }, [])

    const sendEmail = useCallback(
        async (load: CanonicalLoad) => {
            trackLoadContactClicked(load, 'email', 'table_row')
            if (!user) {
                trackEmailValidationFailed({ load, emailType: 'inquiry', errorCode: 'not_authenticated' })
                showSnackbar(SnackbarType.ERROR, 'Please sign in to send emails')
                return
            }

            const testEmail = localStorage.getItem('testEmail')
            const brokerEmail = testEmail || load.brokerContactEmail || load.posterContactEmail
            if (!brokerEmail) {
                trackEmailValidationFailed({ load, emailType: 'inquiry', errorCode: 'recipient_missing' })
                showSnackbar(SnackbarType.ERROR, 'No broker email found for this load')
                return
            }

            const config = currentEmailConfig
            const resolved = resolveEmailAndTemplate(user.nylasEmails || [], config, user.emailTemplates)
            if (!resolved) {
                trackEmailValidationFailed({ load, emailType: 'inquiry', errorCode: 'sender_missing' })
                showSnackbar(SnackbarType.ERROR, 'No email account connected. Please connect an email in settings.')
                return
            }

            const { nylasEmail, template } = resolved

            // Send-only onboarding mailboxes lack the read scope Numeo needs to monitor broker
            // replies — prompt to upgrade the grant, then resume this send after reconnect.
            if (!ensureReadScope(nylasEmail.email, () => sendEmailRef.current?.(load))) {
                trackEmailValidationFailed({ load, emailType: 'inquiry', errorCode: 'read_scope_missing' })
                return
            }

            const uid = getLoadUid(load)

            // Clear any previous error for this load
            setErrorLoads((prev) => {
                if (!prev.has(uid)) return prev
                const next = new Set(prev)
                next.delete(uid)
                return next
            })

            setSendingLoads((prev) => new Set(prev).add(uid))
            trackEmailAttempted(load.sourceType, 'quick')
            const emailAnalytics = trackEmailSendSubmitted({
                load,
                emailType: 'inquiry',
                recipientCount: 1,
                templateId: template?._id,
            })

            try {
                const companyData = resolveSenderCompanyData({
                    selectedCompanyDetail: nylasEmail.companyDetail,
                    nylasEmails: user.nylasEmails,
                    application,
                    companyFromParentEnabled: organizationData?.companyFromParentEnabled,
                    userName: user.name || '',
                })

                const { subject, body: htmlBody } = generateEmailContentFromTemplate(load, user.name || '', template, companyData)
                const loadPayload = convertCanonicalLoadToV4Payload(load)

                const response = await sendPrivateLoadEmailV4({
                    load: loadPayload,
                    action: { type: 'email', callType: 'email' },
                    email: {
                        to: { email: brokerEmail },
                        subject,
                        htmlBody,
                        emailType: template?.type || 'info',
                        emailReplyPreference: 'MANUAL',
                        cc: template?.cc || [],
                        senderEmail: nylasEmail.email,
                    },
                    datSearchId: '',
                    userId: user.id,
                    loadHubAnalytics: toServerEmailAnalytics(emailAnalytics),
                })

                // Capture emailThreadId and loadId from backend response
                const threadId = response?.result?.emailThreadId
                if (threadId) {
                    saveEmailThread(uid, threadId)
                    setEmailThreads((prev) => new Map(prev).set(uid, threadId))
                }
                const mongoId = response?.result?.load?._id
                if (mongoId) {
                    const mongoIdStr = String(mongoId)
                    saveLoadMongoId(uid, mongoIdStr)
                    setLoadMongoIds((prev) => new Map(prev).set(uid, mongoIdStr))
                }

                const alreadySent = response.alreadySent === true
                showSnackbar(SnackbarType.SUCCESS, alreadySent ? response.message || 'Email was already sent for this load' : `Email sent to ${brokerEmail}`)
                trackEmailCompleted(load.sourceType, 'quick', alreadySent ? 'deduplicated' : 'sent')
            } catch (err) {
                const apiMessage = err instanceof AxiosError ? err.response?.data?.message : err instanceof Error ? err.message : ''
                const isAuthError = typeof apiMessage === 'string' && (apiMessage.includes('UNAUTHENTICATED') || apiMessage.includes('reauthenticate'))
                const message = isAuthError
                    ? 'Email connection expired. Please go to Settings and reconnect your email account.'
                    : apiMessage || 'Failed to send email'
                showSnackbar(SnackbarType.ERROR, message)
                trackEmailCompleted(load.sourceType, 'quick', 'failed', { error: err })
                trackEmailRequestFailed(emailAnalytics, 'transport', 'unknown', 'request_failed')
                setErrorLoads((prev) => new Set(prev).add(uid))
                // Auto-clear error after 4 seconds so user can retry
                setTimeout(() => {
                    setErrorLoads((prev) => {
                        if (!prev.has(uid)) return prev
                        const next = new Set(prev)
                        next.delete(uid)
                        return next
                    })
                }, 4000)
            } finally {
                setSendingLoads((prev) => {
                    const next = new Set(prev)
                    next.delete(uid)
                    return next
                })
            }
        },
        [user, application, organizationData?.companyFromParentEnabled, ensureReadScope]
    )

    // Keep a ref to the latest sendEmail so the read-scope guard can resume the send post-reconnect.
    useEffect(() => {
        sendEmailRef.current = sendEmail
    }, [sendEmail])

    const lastSyncKeyRef = useRef<string>('')

    const clearSyncCache = useCallback(() => {
        lastSyncKeyRef.current = ''
    }, [])

    const syncEmailThreads = useCallback(async (loads: CanonicalLoad[]) => {
        const externalIds = loads.map((l) => l.externalId || `PRIVATE|${l.id}`)
        if (!externalIds.length) return

        // Skip if the exact same set of IDs was already synced
        const syncKey = [...externalIds].sort().join('|')
        if (syncKey === lastSyncKeyRef.current) return
        lastSyncKeyRef.current = syncKey

        const threadMap = await fetchEmailThreadIds(externalIds)
        if (!Object.keys(threadMap).length) return

        // Build a lookup: externalId → loadUid (unique per load)
        const extIdToLoadUid = new Map<string, string>()
        for (const load of loads) {
            extIdToLoadUid.set(load.externalId || `PRIVATE|${load.id}`, getLoadUid(load))
        }

        setEmailThreads((prev) => {
            const next = new Map(prev)
            let changed = false
            for (const [extId, data] of Object.entries(threadMap)) {
                const loadUid = extIdToLoadUid.get(extId)
                if (loadUid !== undefined && next.get(loadUid) !== data.emailThreadId) {
                    next.set(loadUid, data.emailThreadId)
                    saveEmailThread(loadUid, data.emailThreadId)
                    changed = true
                }
            }
            return changed ? next : prev
        })

        setUnreadCounts((prev) => {
            const next = new Map(prev)
            let changed = false
            for (const [extId, data] of Object.entries(threadMap)) {
                const loadUid = extIdToLoadUid.get(extId)
                if (loadUid !== undefined && data.unreadCount > 0 && next.get(loadUid) !== data.unreadCount) {
                    next.set(loadUid, data.unreadCount)
                    changed = true
                }
            }
            return changed ? next : prev
        })

        setLoadMongoIds((prev) => {
            const next = new Map(prev)
            let changed = false
            for (const [extId, data] of Object.entries(threadMap)) {
                const loadUid = extIdToLoadUid.get(extId)
                if (loadUid !== undefined && data.loadId && next.get(loadUid) !== data.loadId) {
                    next.set(loadUid, data.loadId)
                    saveLoadMongoId(loadUid, data.loadId)
                    changed = true
                }
            }
            return changed ? next : prev
        })
    }, [])

    const resetUnread = useCallback((loadUid: string) => {
        setUnreadCounts((prev) => {
            if (!prev.has(loadUid) || prev.get(loadUid) === 0) return prev
            const next = new Map(prev)
            next.set(loadUid, 0)
            return next
        })
    }, [])

    return { sendEmail, sendingLoads, errorLoads, emailThreads, unreadCounts, resetUnread, syncEmailThreads, clearSyncCache, loadMongoIds, updateEmailConfig }
}
